import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { Circle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui';
import { PublicClubLayout } from '@/components/layout/PublicClubLayout';
import { PublicOrgLayout } from '@/components/layout/PublicOrgLayout';
import { EmbedHostProvider } from '@/contexts/EmbedHostContext';
import { useOrgPublicProfile } from '@/contexts/OrgPublicProfileContext';
import { useClubHost } from '@/contexts/ClubHostContext';
import { useOrgHost } from '@/contexts/OrgHostContext';
import { useClubSlug } from '@/hooks/useClubSlug';
import { usePublicClubProfile } from '@/queries/publicClubProfile';

function PublicClubRouteShellContent({ children }: { children: ReactNode }) {
  const { slug: legacyClubSlug, orgSlug: orgSlugParam } = useParams<{
    slug?: string;
    orgSlug?: string;
  }>();
  const { host: orgHost, organization: orgHostOrganization } = useOrgHost();
  const isSubdomainOrgTenant =
    orgHost.mode === 'subdomain' && Boolean(orgHostOrganization);
  const isUnifiedOrgRoute =
    (Boolean(orgSlugParam) && !legacyClubSlug) || isSubdomainOrgTenant;
  const { profile: orgProfile, isLoading: orgProfileLoading, error: orgProfileError } =
    useOrgPublicProfile();

  const clubSlug = useClubSlug();
  const { host: clubHost, loading: clubHostLoading } = useClubHost();
  const {
    profile: legacyClubProfile,
    isLoading: legacyClubProfileLoading,
    error: legacyClubProfileError,
  } = usePublicClubProfile({
    slug: clubSlug,
    subdomainLabel:
      !isUnifiedOrgRoute && clubHost.mode === 'subdomain' ? clubHost.subdomainLabel : undefined,
    hostLoading: !isUnifiedOrgRoute && clubHost.mode === 'subdomain' && clubHostLoading,
    enabled:
      !isUnifiedOrgRoute &&
      (!!clubSlug || (clubHost.mode === 'subdomain' && !!clubHost.subdomainLabel)),
  });

  const isLoading = isUnifiedOrgRoute ? orgProfileLoading : legacyClubProfileLoading;

  if (isLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center p-6">
        <Loader2 className="h-8 w-8 animate-spin text-foreground" aria-label="Loading" />
      </main>
    );
  }

  if (isUnifiedOrgRoute) {
    if (orgProfileError || !orgProfile) {
      return (
        <main className="flex min-h-screen flex-col items-center justify-center p-6 text-center">
          <Circle className="mb-4 h-12 w-12 text-muted-foreground" aria-hidden />
          <h1 className="text-xl font-semibold text-foreground">Organization not found</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {orgProfileError || 'This page is unavailable.'}
          </p>
          <Button className="mt-4" onClick={() => window.location.assign('/')}>
            Go home
          </Button>
        </main>
      );
    }

    return <PublicOrgLayout profile={orgProfile}>{children}</PublicOrgLayout>;
  }

  if (legacyClubProfileError || !legacyClubProfile) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center p-6 text-center">
        <Circle className="mb-4 h-12 w-12 text-muted-foreground" aria-hidden />
        <h1 className="text-xl font-semibold text-foreground">Club not found</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {legacyClubProfileError || 'This club page is unavailable.'}
        </p>
        <Button className="mt-4" onClick={() => window.location.assign('/')}>
          Go home
        </Button>
      </main>
    );
  }

  return <PublicClubLayout profile={legacyClubProfile}>{children}</PublicClubLayout>;
}

export function PublicClubRouteShell({ children }: { children: ReactNode }) {
  return (
    <EmbedHostProvider>
      <PublicClubRouteShellContent>{children}</PublicClubRouteShellContent>
    </EmbedHostProvider>
  );
}
